import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Heart, Mail, Lock, Smartphone, User, Calendar, MapPin, Building2, ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { authApi } from '../../lib/api/auth';
import { UserRole as BackendUserRole, Gender } from '../../lib/api/types';
import { useHospitals } from '../../lib/hooks/useHospitals';

type UserRole = 'patient' | 'doctor' | 'physiotherapist' | 'coordinator' | 'nurse' | 'physician_assistant';

interface SignUpScreenProps {
  onSignUpComplete: (role: UserRole, name: string) => void;
  onBackToLogin: () => void;
  preSelectedRole?: UserRole;
}

export function SignUpScreen({ onSignUpComplete, onBackToLogin, preSelectedRole = 'patient' }: SignUpScreenProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole>(preSelectedRole);
  const [step, setStep] = useState(1); // 1: Role Selection, 2: Demographics, 3: Hospital & Password
  
  // Fetch hospitals from API
  const { data: hospitals = [], isLoading: hospitalsLoading } = useHospitals({ is_active: true });
  
  // Form fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');
  const [hospital, setHospital] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleNext = () => {
    // Clear previous errors
    setError(null);
    
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      // Validate demographics with detailed error messages
      if (!fullName.trim()) {
        const errorMsg = 'Please enter your full name';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }
      if (!email.trim()) {
        const errorMsg = 'Please enter your email address';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }
      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        const errorMsg = 'Please enter a valid email address';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }
      if (!phoneNumber.trim()) {
        const errorMsg = 'Please enter your phone number';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }
      // Phone number validation (basic)
      const phoneRegex = /^[\d\s\+\-()]+$/;
      if (!phoneRegex.test(phoneNumber.trim())) {
        const errorMsg = 'Please enter a valid phone number';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }
      if (!dateOfBirth) {
        const errorMsg = 'Please select your date of birth';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }
      if (!gender) {
        const errorMsg = 'Please select your gender';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }
      if (!address.trim()) {
        const errorMsg = 'Please enter your address';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }
      if (!city.trim()) {
        const errorMsg = 'Please enter your city';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }
      if (!pincode.trim()) {
        const errorMsg = 'Please enter your pincode';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }
      setStep(3);
    } else if (step === 3) {
      handleSignUp();
    }
  };

  const handleSignUp = async () => {
    // Clear previous errors
    setError(null);
    
    // Validate final step with detailed error messages
    if (selectedRole !== 'coordinator' && !hospital) {
      const errorMsg = 'Please select a hospital';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    if (!password) {
      const errorMsg = 'Please enter a password';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    if (password.length < 8) {
      const errorMsg = 'Password must be at least 8 characters long';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    if (!confirmPassword) {
      const errorMsg = 'Please confirm your password';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    if (password !== confirmPassword) {
      const errorMsg = 'Passwords do not match. Please ensure both passwords are identical.';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    // Show loading state
    setIsLoading(true);
    
    try {
      // Split full name into first and last name
      const nameParts = fullName.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || nameParts[0];

      // Map frontend role to backend role enum
      const roleMap: Record<UserRole, keyof typeof BackendUserRole> = {
        'patient': 'PATIENT',
        'doctor': 'DOCTOR',
        'physiotherapist': 'THERAPIST',
        'coordinator': 'COORDINATOR',
        'nurse': 'NURSE',
        'physician_assistant': 'PHYSICIAN_ASSISTANT',
      };

      // Map gender to backend Gender enum
      const genderMap: Record<string, Gender> = {
        'male': Gender.MALE,
        'female': Gender.FEMALE,
        'other': Gender.OTHER,
      };

      // Build registration data based on role
      const baseRegistrationData = {
        email: email.trim(),
        password: password,
        first_name: firstName,
        last_name: lastName,
        phone: phoneNumber.trim(),
        role: BackendUserRole[roleMap[selectedRole]],
      };

      // Add role-specific fields
      const registrationData: any = { ...baseRegistrationData };

      // Add hospital_id for doctors, therapists, nurses, and PAs
      if (['doctor', 'physiotherapist', 'nurse', 'physician_assistant'].includes(selectedRole)) {
        registrationData.hospital_id = hospital ? parseInt(hospital) : undefined;
      }

      // Add patient-specific fields only for patients
      if (selectedRole === 'patient') {
        registrationData.date_of_birth = dateOfBirth;
        registrationData.gender = genderMap[gender];
        registrationData.address = `${address}, ${city}, ${pincode}`;
        registrationData.city = city;
        registrationData.pincode = pincode;
      }

      // Call backend API - use registerAndLogin for self-registration
      const response = await authApi.registerAndLogin(registrationData);

      // Success! Tokens are already stored by authApi.registerAndLogin()
      toast.success('Account created successfully!', {
        description: 'Redirecting to your dashboard...',
      });
      
      // Redirect to appropriate dashboard
      setTimeout(() => {
        onSignUpComplete(selectedRole, fullName);
      }, 1000);
    } catch (error: any) {
      console.error('Registration error:', error);
      
      // Extract error message from different error formats
      let errorMessage = 'Failed to create account. Please try again.';
      
      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Handle specific error cases with user-friendly messages
      if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('already')) {
        errorMessage = 'This email address is already registered. Please use a different email or try logging in.';
      } else if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('exists')) {
        errorMessage = 'An account with this information already exists. Please check your details or try logging in.';
      } else if (errorMessage.toLowerCase().includes('invalid email')) {
        errorMessage = 'Please enter a valid email address.';
      } else if (errorMessage.toLowerCase().includes('password')) {
        errorMessage = 'Password does not meet requirements. Please use at least 8 characters.';
      } else if (errorMessage.toLowerCase().includes('cannot connect') || errorMessage.toLowerCase().includes('network')) {
        errorMessage = 'Cannot connect to server. Please check your internet connection and try again.';
      }
      
      setError(errorMessage);
      toast.error('Registration Failed', {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleDisplayName = (role: UserRole) => {
    switch (role) {
      case 'patient': return 'Patient';
      case 'doctor': return 'Pulmonologist';
      case 'physiotherapist': return 'Physiotherapist';
      case 'coordinator': return 'Care Coordinator';
      case 'nurse': return 'Home Nurse';
      case 'physician_assistant': return 'Physician Assistant';
      default: return role;
    }
  };

  return (
    <div className="min-h-screen bg-[#D4F0F8]">
      {/* Header */}
      <div className="bg-[#D4F0F8] p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center mx-auto">
            <Heart className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-primary">Pulmonology Spectrum by Healthkon</h1>
          <p className="text-muted-foreground">Create Your Account</p>
        </div>
      </div>

      {/* Sign Up Form */}
      <div className="p-6 max-w-md mx-auto pb-20">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
              1
            </div>
            <div className={`w-16 h-1 ${step >= 2 ? 'bg-primary' : 'bg-secondary'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
              2
            </div>
            <div className={`w-16 h-1 ${step >= 3 ? 'bg-primary' : 'bg-secondary'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
              3
            </div>
          </div>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              {step > 1 && (
                <button onClick={() => setStep(step - 1)} className="text-primary">
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div className="flex-1">
                <CardTitle className="text-primary">
                  {step === 1 && 'Select Your Role'}
                  {step === 2 && 'Personal Information'}
                  {step === 3 && 'Hospital & Security'}
                </CardTitle>
                <CardDescription>
                  {step === 1 && 'Choose your role in the healthcare system'}
                  {step === 2 && 'Enter your basic details'}
                  {step === 3 && 'Complete your registration'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            {/* Step 1: Role Selection */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant={selectedRole === 'patient' ? 'default' : 'outline'}
                    className={`h-24 flex flex-col gap-2 ${selectedRole === 'patient' ? 'bg-primary' : ''}`}
                    onClick={() => setSelectedRole('patient')}
                  >
                    <Heart className="w-6 h-6" />
                    <span>Patient</span>
                  </Button>
                  <Button
                    variant={selectedRole === 'doctor' ? 'default' : 'outline'}
                    className={`h-24 flex flex-col gap-2 ${selectedRole === 'doctor' ? 'bg-primary' : ''}`}
                    onClick={() => setSelectedRole('doctor')}
                  >
                    <User className="w-6 h-6" />
                    <span>Pulmonologist</span>
                  </Button>
                  <Button
                    variant={selectedRole === 'physiotherapist' ? 'default' : 'outline'}
                    className={`h-24 flex flex-col gap-2 ${selectedRole === 'physiotherapist' ? 'bg-primary' : ''}`}
                    onClick={() => setSelectedRole('physiotherapist')}
                  >
                    <User className="w-6 h-6" />
                    <span>Physiotherapist</span>
                  </Button>
                  <Button
                    variant={selectedRole === 'coordinator' ? 'default' : 'outline'}
                    className={`h-24 flex flex-col gap-2 ${selectedRole === 'coordinator' ? 'bg-primary' : ''}`}
                    onClick={() => setSelectedRole('coordinator')}
                  >
                    <User className="w-6 h-6" />
                    <span>Care Coordinator</span>
                  </Button>
                  <Button
                    variant={selectedRole === 'nurse' ? 'default' : 'outline'}
                    className={`h-24 flex flex-col gap-2 ${selectedRole === 'nurse' ? 'bg-primary' : ''}`}
                    onClick={() => setSelectedRole('nurse')}
                  >
                    <User className="w-6 h-6" />
                    <span>Home Nurse</span>
                  </Button>
                  <Button
                    variant={selectedRole === 'physician_assistant' ? 'default' : 'outline'}
                    className={`h-24 flex flex-col gap-2 ${selectedRole === 'physician_assistant' ? 'bg-primary' : ''}`}
                    onClick={() => setSelectedRole('physician_assistant')}
                  >
                    <User className="w-6 h-6" />
                    <span>Physician Assistant</span>
                  </Button>
                </div>

                <div className="bg-accent/10 p-4 rounded-lg">
                  <p className="text-muted-foreground">
                    You've selected: <strong className="text-primary">{getRoleDisplayName(selectedRole)}</strong>
                  </p>
                </div>

                <Button 
                  className="w-full bg-accent hover:bg-accent/90 rounded-xl h-12"
                  onClick={handleNext}
                >
                  Continue
                </Button>
              </div>
            )}

            {/* Step 2: Demographics */}
            {step === 2 && (
              <div className="space-y-4">
                {/* Error Alert */}
                {error && (
                  <Alert variant="destructive" className="mb-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="Enter your full name"
                      value={fullName}
                      onChange={(e) => {
                        setFullName(e.target.value);
                        if (error) setError(null); // Clear error when user types
                      }}
                      className="pl-10 rounded-xl"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError(null); // Clear error when user types
                      }}
                      className="pl-10 rounded-xl"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <div className="relative">
                    <Smartphone className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+91 XXXXX XXXXX"
                      value={phoneNumber}
                      onChange={(e) => {
                        setPhoneNumber(e.target.value);
                        if (error) setError(null); // Clear error when user types
                      }}
                      className="pl-10 rounded-xl"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="dob">Date of Birth *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="dob"
                        type="date"
                        value={dateOfBirth}
                        onChange={(e) => {
                          setDateOfBirth(e.target.value);
                          if (error) setError(null); // Clear error when user types
                        }}
                        className="pl-10 rounded-xl"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender *</Label>
                    <Select value={gender} onValueChange={(value) => {
                      setGender(value);
                      if (error) setError(null); // Clear error when user selects
                    }}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="address"
                      type="text"
                      placeholder="Street address"
                      value={address}
                      onChange={(e) => {
                        setAddress(e.target.value);
                        if (error) setError(null); // Clear error when user types
                      }}
                      className="pl-10 rounded-xl"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      type="text"
                      placeholder="City"
                      value={city}
                      onChange={(e) => {
                        setCity(e.target.value);
                        if (error) setError(null); // Clear error when user types
                      }}
                      className="rounded-xl"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                      id="pincode"
                      type="text"
                      placeholder="Pincode"
                      value={pincode}
                      onChange={(e) => {
                        setPincode(e.target.value);
                        if (error) setError(null); // Clear error when user types
                      }}
                      className="rounded-xl"
                      required
                    />
                  </div>
                </div>

                <Button 
                  className="w-full bg-accent hover:bg-accent/90 rounded-xl h-12"
                  onClick={handleNext}
                >
                  Continue
                </Button>
              </div>
            )}

            {/* Step 3: Hospital & Password */}
            {step === 3 && (
              <div className="space-y-4">
                {/* Error Alert */}
                {error && (
                  <Alert variant="destructive" className="mb-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                
                {selectedRole !== 'coordinator' && (
                  <div className="space-y-2">
                    <Label htmlFor="hospital">Hospital Affiliation *</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-3 w-4 h-4 text-muted-foreground z-10" />
                      <Select value={hospital} onValueChange={(value) => {
                        setHospital(value);
                        if (error) setError(null); // Clear error when user selects
                      }}>
                        <SelectTrigger className="rounded-xl pl-10">
                          <SelectValue placeholder="Select your hospital" />
                        </SelectTrigger>
                        <SelectContent>
                          {hospitals.map((hosp) => (
                            <SelectItem key={hosp.id} value={hosp.id.toString()}>
                              {hosp.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-muted-foreground">
                      Select the hospital where you practice or receive care
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">Create Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (error) setError(null); // Clear error when user types
                      }}
                      className="pl-10 rounded-xl"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Re-enter password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (error) setError(null); // Clear error when user types
                      }}
                      className="pl-10 rounded-xl"
                      required
                    />
                  </div>
                </div>

                <div className="bg-accent/10 p-4 rounded-lg space-y-2">
                  <p className="text-muted-foreground">
                    <strong>Registration Summary:</strong>
                  </p>
                  <p className="text-muted-foreground">
                    Role: <strong className="text-foreground">{getRoleDisplayName(selectedRole)}</strong>
                  </p>
                  <p className="text-muted-foreground">
                    Name: <strong className="text-foreground">{fullName}</strong>
                  </p>
                  <p className="text-muted-foreground">
                    Email: <strong className="text-foreground">{email}</strong>
                  </p>
                  {selectedRole !== 'coordinator' && hospital && (
                    <p className="text-muted-foreground">
                      Hospital: <strong className="text-foreground">{hospitals.find(hosp => hosp.id.toString() === hospital)?.name}</strong>
                    </p>
                  )}
                </div>

                <Button 
                  className="w-full bg-accent hover:bg-accent/90 rounded-xl h-12"
                  onClick={handleNext}
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </div>
            )}

            {/* Back to Login */}
            <div className="mt-6 text-center">
              <Button
                variant="link"
                className="text-primary"
                onClick={onBackToLogin}
              >
                Already have an account? Login
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground">
            Powered by Healthkon
          </p>
          <p className="text-muted-foreground">
            Anytime – Anywhere – Healthcare
          </p>
        </div>
      </div>
    </div>
  );
}